import { useEffect, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import AgentJobInspector, { type AgentJobInspectorLinks } from './AgentJobInspector'
import { agentJobsAPI } from '@/services/api/agentJobs'
import type { AgentJobEvent } from '@/types/agentJob'

export interface AgentJobDetailPanelProps {
  organizationId?: string | null
  /** Null ou absent : routes de portée organisation. */
  applicationId?: string | null
  jobId?: string | null
  onSelectedJob?: (jobId?: string) => void
  buildRetryParentHref?: (jobId: string) => string
  /** Liens des sections Approbations et Provenance (routes de la page). */
  links?: Partial<AgentJobInspectorLinks>
}

export default function AgentJobDetailPanel({
  organizationId,
  applicationId,
  jobId,
  onSelectedJob,
  buildRetryParentHref,
  links,
}: AgentJobDetailPanelProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Sans Application : job d'un graphe en portée organisation (routes organisation).
  const scopeReady = Boolean(organizationId)
  const selectedJobId = jobId || null
  const scope = [organizationId, applicationId, selectedJobId] as const
  const eventScopeKey = `${organizationId ?? ''}:${applicationId ?? ''}:${selectedJobId ?? ''}`
  const eventsCursorRef = useRef<{ scopeKey: string; cursor?: { sequence?: number; event_id?: string } }>({ scopeKey: '' })
  const eventsByJobRef = useRef(new Map<string, Map<string, AgentJobEvent>>())
  if (eventsCursorRef.current.scopeKey !== eventScopeKey) {
    eventsCursorRef.current = { scopeKey: eventScopeKey }
  }

  const detailQuery = useQuery({
    queryKey: ['agent-job', ...scope],
    queryFn: () => agentJobsAPI.get(organizationId!, applicationId, selectedJobId!),
    enabled: scopeReady && Boolean(selectedJobId),
    refetchInterval: 2000,
    retry: false,
  })
  const resultQuery = useQuery({
    queryKey: ['agent-job-result', ...scope],
    queryFn: () => agentJobsAPI.result(organizationId!, applicationId, selectedJobId!),
    enabled: scopeReady && Boolean(selectedJobId),
    refetchInterval: 2000,
    retry: false,
  })
  const artifactsQuery = useQuery({
    queryKey: ['agent-job-artifacts', ...scope],
    queryFn: () => agentJobsAPI.artifacts(organizationId!, applicationId, selectedJobId!),
    enabled: scopeReady && Boolean(selectedJobId),
    refetchInterval: 2000,
    retry: false,
  })
  const costQuery = useQuery({
    queryKey: ['agent-job-cost', ...scope],
    queryFn: () => agentJobsAPI.cost(organizationId!, applicationId, selectedJobId!),
    enabled: scopeReady && Boolean(selectedJobId),
    refetchInterval: 2000,
    retry: false,
  })
  const eventsQuery = useQuery({
    queryKey: ['agent-job-events', ...scope],
    queryFn: async () => {
      if (!selectedJobId) return { events: [] }
      const requestScopeKey = eventScopeKey
      const events: AgentJobEvent[] = []
      let cursor = eventsCursorRef.current.cursor
      let complete = false
      for (let page = 0; page < 10; page += 1) {
        const response = await agentJobsAPI.events(organizationId!, applicationId, selectedJobId!, cursor)
        events.push(...(response.events ?? []))
        const next = response.cursor
        if (!next || !response.events?.length) {
          complete = true
          break
        }
        if (next.sequence === cursor?.sequence && next.event_id === cursor?.event_id) {
          throw new Error('agent_job_events_pagination_incomplete')
        }
        cursor = next
      }
      const accumulated = eventsByJobRef.current.get(requestScopeKey) ?? new Map<string, AgentJobEvent>()
      for (const event of events) accumulated.set(`${event.sequence}:${event.event_id}`, event)
      if (eventsCursorRef.current.scopeKey !== requestScopeKey) return { events: [...accumulated.values()] }
      eventsCursorRef.current.cursor = cursor
      eventsByJobRef.current.set(requestScopeKey, accumulated)
      return { events: [...accumulated.values()], hasMore: !complete }
    },
    enabled: scopeReady && Boolean(selectedJobId),
    refetchInterval: 2000,
    retry: false,
  })
  const events = useMemo(() => {
    const unique = new Map<string, AgentJobEvent>()
    for (const event of eventsQuery.data?.events ?? []) unique.set(`${event.sequence}:${event.event_id}`, event)
    return [...unique.values()].sort((a, b) => a.sequence - b.sequence)
  }, [eventsQuery.data?.events])

  const clearSelection = () => onSelectedJob?.(undefined)
  useEffect(() => {
    if (!detailQuery.isError || !selectedJobId) return
    eventsByJobRef.current.delete(eventScopeKey)
    if (eventsCursorRef.current.scopeKey === eventScopeKey) eventsCursorRef.current = { scopeKey: '' }
    void Promise.all([
      queryClient.removeQueries({ queryKey: ['agent-job', ...scope] }),
      queryClient.removeQueries({ queryKey: ['agent-job-result', ...scope] }),
      queryClient.removeQueries({ queryKey: ['agent-job-artifacts', ...scope] }),
      queryClient.removeQueries({ queryKey: ['agent-job-cost', ...scope] }),
      queryClient.removeQueries({ queryKey: ['agent-job-events', ...scope] }),
    ])
    clearSelection()
  }, [detailQuery.isError, selectedJobId, organizationId, applicationId, eventScopeKey, queryClient])

  const cancelMutation = useMutation({
    mutationFn: () => agentJobsAPI.cancel(organizationId!, applicationId, selectedJobId!),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent-job', ...scope] })
      void queryClient.invalidateQueries({ queryKey: ['agent-jobs', organizationId, applicationId] })
    },
  })
  const retryMutation = useMutation({
    mutationFn: () => agentJobsAPI.retry(organizationId!, applicationId, selectedJobId!),
    onSuccess: (response) => {
      const nextJobId = response.job?.job.job_id
      if (nextJobId) onSelectedJob?.(nextJobId)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent-job', ...scope] })
      void queryClient.invalidateQueries({ queryKey: ['agent-jobs', organizationId, applicationId] })
    },
  })

  const parentJobId = detailQuery.data?.job.job.retry_of_job_id
  const retryParentHref = parentJobId ? buildRetryParentHref?.(parentJobId) : undefined
  return (
    <div>
    <AgentJobInspector
      key={`${organizationId}:${applicationId}:${selectedJobId}`}
      retryParentHref={retryParentHref}
      links={links}
      detail={detailQuery.isError ? undefined : detailQuery.data}
      result={resultQuery.isError || detailQuery.isError ? undefined : (resultQuery.data?.result ?? detailQuery.data?.job.result)}
      artifacts={artifactsQuery.isError || detailQuery.isError ? [] : (artifactsQuery.data?.artifacts ?? detailQuery.data?.job.result?.artifacts)}
      cost={costQuery.isError || detailQuery.isError ? undefined : costQuery.data}
      events={eventsQuery.isError || detailQuery.isError ? [] : events}
      isLoading={detailQuery.isLoading}
      isCancelling={cancelMutation.isPending}
      isRetrying={retryMutation.isPending}
      error={detailQuery.error || cancelMutation.error || retryMutation.error ? t('agentJob.error') : null}
      onCancel={() => cancelMutation.mutate()}
      onRetry={() => retryMutation.mutate()}
    />
    {eventsQuery.data?.hasMore ? <p className="mt-2 text-xs text-muted-foreground">{t('agentJob.loading')}</p> : null}
    </div>
  )
}
